import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  createClientOrganization,
  getClientOrganization,
  updateClientOrganization,
} from "../../../api/adminClientManagementApi";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminStatePanel from "../../../components/midpoint/admin/layout/AdminStatePanel";
import RequirePermission from "../../../auth/RequirePermission";

const EMPTY = {
  clientName: "",
  slug: "",
  status: "ACTIVE",
  primaryContactName: "",
  primaryContactEmail: "",
  billingEmail: "",
  notes: "",
  statusReason: "",
};

function AdminClientOrganizationFormPageContent() {
  const { clientOrgId } = useParams();
  const isNew = !clientOrgId;
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (isNew) return;
    getClientOrganization(clientOrgId)
      .then((body) => {
        const o = body.organization;
        setForm({
          clientName: o.clientName || "",
          slug: o.slug || "",
          status: o.status || "ACTIVE",
          primaryContactName: o.primaryContactName || "",
          primaryContactEmail: o.primaryContactEmail || "",
          billingEmail: o.billingEmail || "",
          notes: o.notes || "",
          statusReason: o.statusReason || "",
        });
      })
      .catch((e) => setErrorMessage(e.message || "Failed to load organization."))
      .finally(() => setLoading(false));
  }, [clientOrgId, isNew]);

  const update = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setErrorMessage("");
    try {
      if (isNew) {
        const body = await createClientOrganization(form);
        navigate(`/admin/client-organizations/${body.organization.clientOrgId}`, { replace: true });
      } else {
        await updateClientOrganization(clientOrgId, form);
        navigate(`/admin/client-organizations/${clientOrgId}`);
      }
    } catch (e) {
      setErrorMessage(e.message || "Failed to save organization.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminPageLayout>
        <AdminStatePanel message="Loading…" />
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout>
      <AdminPageHeader
        title={isNew ? "New Client Organization" : "Edit Organization"}
        description={isNew ? "Create a client portal organization" : form.clientName}
      />

      {errorMessage ? <AdminStatePanel tone="error" message={errorMessage} /> : null}

      <form onSubmit={handleSubmit} className="panel-card client-community-form">
        <label className="admin-users-field">
          <span>Client name *</span>
          <input value={form.clientName} onChange={update("clientName")} required />
        </label>
        <label className="admin-users-field">
          <span>Slug</span>
          <input value={form.slug} onChange={update("slug")} placeholder="auto-from-name" />
        </label>
        <label className="admin-users-field">
          <span>Status</span>
          <select value={form.status} onChange={update("status")}>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
            <option value="SUSPENDED">Suspended</option>
          </select>
        </label>
        <label className="admin-users-field">
          <span>Status reason</span>
          <textarea value={form.statusReason} onChange={update("statusReason")} rows={2} />
        </label>
        <label className="admin-users-field">
          <span>Primary contact name</span>
          <input value={form.primaryContactName} onChange={update("primaryContactName")} />
        </label>
        <label className="admin-users-field">
          <span>Primary contact email</span>
          <input type="email" value={form.primaryContactEmail} onChange={update("primaryContactEmail")} />
        </label>
        <label className="admin-users-field">
          <span>Billing email</span>
          <input type="email" value={form.billingEmail} onChange={update("billingEmail")} />
        </label>
        <label className="admin-users-field">
          <span>Internal notes</span>
          <textarea value={form.notes} onChange={update("notes")} rows={4} />
        </label>
        <div className="client-view-card__actions">
          <button type="submit" className="midpoint-share-button" disabled={saving}>
            {saving ? "Saving…" : isNew ? "Create" : "Save"}
          </button>
          <Link
            to={isNew ? "/admin/client-organizations" : `/admin/client-organizations/${clientOrgId}`}
            className="cv-button-link"
          >
            Cancel
          </Link>
        </div>
      </form>
    </AdminPageLayout>
  );
}

const AdminClientOrganizationFormPage = () => (
  <RequirePermission permission="client_admin.manage_organizations">
    <AdminClientOrganizationFormPageContent />
  </RequirePermission>
);

export default AdminClientOrganizationFormPage;
